import { createI18n } from 'vue-i18n'
import { App } from 'vue'

// Import language files
import es from '@/locales/es.json'
import en from '@/locales/en.json'

type LocaleCode = 'es' | 'en'

function getDefaultLocale(): LocaleCode {
  // Try saved preference
  try {
    if (typeof window !== 'undefined' && 'localStorage' in window) {
      const saved = window.localStorage.getItem('user-locale')
      if (saved === 'es' || saved === 'en') return saved
    }
  } catch { /* storage may be blocked */ }

  // 2) browser language (SSR-safe)
  const navLang =
    (globalThis.navigator?.languages?.[0] ??
      globalThis.navigator?.language ??
      '')
      .toLowerCase()

  return navLang.startsWith('es') ? 'es' : 'es' // your default is Spanish
}


// Create i18n instance
export const i18n = createI18n({
  legacy: false, // Use Composition API
  locale: getDefaultLocale(),
  fallbackLocale: 'en', // Fallback to English if Spanish translation is missing
  globalInjection: true, // Enable global $t helper
  messages: { es, en },
  // Enable missing translation warnings in development
  missing: (locale: string, key: string) => {
    if (import.meta.env.DEV) {
      console.warn(`Missing translation for key "${key}" in locale "${locale}"`)
    }
  },
  // Date/time formatting
  datetimeFormats: {
    es: {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      time: {
        hour: 'numeric',
        minute: 'numeric'
      },
      datetime: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      }
    },
    en: {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      time: {
        hour: 'numeric',
        minute: 'numeric'
      },
      datetime: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      }
    }
  },
  // Number formatting
  numberFormats: {
    es: {
      currency: {
        style: 'currency',
        currency: 'COP', // Colombian Peso
        notation: 'standard'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      },
      distance: {
        style: 'unit',
        unit: 'kilometer',
        unitDisplay: 'short'
      }
    },
    en: {
      currency: {
        style: 'currency',
        currency: 'USD',
        notation: 'standard'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      },
      distance: {
        style: 'unit',
        unit: 'kilometer',
        unitDisplay: 'short'
      }
    }
  }
})

// Plugin installation function
export function installI18n(app: App) {
  app.use(i18n)
}

// Helper to change locale and persist to localStorage
export function setLocale(locale: LocaleCode) {
  if (locale !== 'es' && locale !== 'en') return
  i18n.global.locale.value = locale

  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('user-locale', locale)
    }
  } catch {
    /* ignore */
  }

  if (typeof document !== 'undefined') {
    // Update document lang attribute for accessibility
    document.documentElement.lang = locale

    // Update page title if available
    const titleKey = 'app.name'
    if (i18n.global.te(titleKey)) {
      document.title = i18n.global.t(titleKey)
    }
  }
}

// Get current locale
export function getCurrentLocale(): LocaleCode {
  return i18n.global.locale.value as LocaleCode
}

// Get available locales
export function getAvailableLocales(): Array<{ code: LocaleCode; name: string; nativeName: string }> {
  return [
    { code: 'es', name: 'Spanish', nativeName: 'Español' },
    { code: 'en', name: 'English', nativeName: 'English' }
  ]
}

export function translate(key: string, params?: Record<string, any>): string {
  return i18n.global.t(key, params ?? {})
}

export function formatDate(date: Date | number, format: string = 'short'): string {
  return i18n.global.d(date, format)
}

export function formatNumber(num: number, format: string = 'decimal'): string {
  return i18n.global.n(num, format)
}